import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Project, ProjectDetail } from '../types';
import { getProjectById, getProjectDetails } from '../services/projectService';
import SectionContainer from '../components/SectionContainer';
import Button from '../components/Button';

// Styles pour la page de détail du projet
const ProjectDetailContainer = styled.div`
  padding-top: 120px;
  padding-bottom: var(--spacing-xl);
`;

const ProjectHeader = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const ProjectTitle = styled.h1`
  font-family: var(--font-heading);
  font-weight: var(--font-weight-light);
  font-size: 2.8rem;
  margin-bottom: var(--spacing-sm);
  text-align: center;
  &::first-letter {
    color: #C13221;
  }
`;

const ProjectLocation = styled.h2`
  font-family: var(--font-primary);
  font-weight: var(--font-weight-light);
  font-size: 1.4rem;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const ProjectYear = styled.span`
  font-family: var(--font-primary);
  font-style: italic;
  margin-left: var(--spacing-sm);
`;

const ProjectCategory = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-md);
  font-size: 1rem;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const MainImageContainer = styled.div`
  width: 100%;
  height: 70vh;
  margin-bottom: var(--spacing-lg);
  overflow: hidden;
  position: relative;
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: none; /* Assurer que l'image est en couleur */
`;

const ProjectContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectInfo = styled.div`
`;

const ProjectDescription = styled.div`
  margin-bottom: var(--spacing-lg);
  line-height: 1.8;
  color: var(--color-text-secondary);
  font-size: 1.1rem;
`;

const ProjectKeyPoints = styled.ul`
  margin-bottom: var(--spacing-lg);
  padding-left: 0;
  list-style-type: none;
  
  li {
    margin-bottom: 10px;
    color: var(--color-text-secondary);
  }
`;

const DetailsList = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: var(--spacing-md);
  align-items: start;
  padding: var(--spacing-xs) 0;
  border-bottom: 1px solid var(--color-light-gray);
`;

const DetailLabel = styled.div`
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const DetailValue = styled.div`
  color: var(--color-text-secondary);
  line-height: 1.6;
`;

const ProjectGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryItem = styled.div`
  height: 300px;
  overflow: hidden;
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  filter: none; /* Assurer que l'image est en couleur */
`;

const BackButton = styled.div`
  margin-top: var(--spacing-lg);
  text-align: center;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 150px 0;
  font-size: 1.2rem;
  color: var(--color-text-secondary);
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 150px 0;
  font-size: 1.2rem;
  color: #e74c3c;
`;

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<ProjectDetail[]>([]);
  
  useEffect(() => {
    const loadProject = async () => {
      if (!id) {
        setError("ID du projet non spécifié");
        setLoading(false);
        return;
      }
      
      try {
        const projectData = await getProjectById(id);
        setProject(projectData);
        // Charger les détails du projet depuis la table dédiée
        const fetchedDetails = await getProjectDetails(id);
        setDetails(fetchedDetails || []);
        setLoading(false);
      } catch (err) {
        console.error("Erreur lors du chargement du projet:", err);
        setError("Impossible de charger les détails du projet");
        setLoading(false);
      }
    };
    
    loadProject();
  }, [id]);
  
  if (loading) {
    return <LoadingMessage>Chargement du projet...</LoadingMessage>;
  }
  
  if (error || !project) {
    return <ErrorMessage>{error || "Projet non trouvé"}</ErrorMessage>;
  }
  
  return (
    <ProjectDetailContainer>
      <SectionContainer>
        <ProjectHeader>
          <ProjectCategory>{project.category}</ProjectCategory>
          <ProjectTitle>{project.title}</ProjectTitle>
          <ProjectLocation>
            {project.location}
          </ProjectLocation>
        </ProjectHeader>
        
        <MainImageContainer>
          <MainImage src={project.mainImage} alt={project.title} />
        </MainImageContainer>
        
        <ProjectContent>
          <ProjectInfo>
            <ProjectDescription>
              {project.description}
            </ProjectDescription>
            
            {project.keyPoints && project.keyPoints.length > 0 && (
              <ProjectKeyPoints>
                {project.keyPoints.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ProjectKeyPoints>
            )}

            {details && details.length > 0 && (
              <DetailsList>
                {details
                  .slice()
                  .sort((a, b) => (a.position || 0) - (b.position || 0))
                  .map((detail, index) => (
                    <DetailRow key={detail.id || index}>
                      <DetailLabel>
                        <span style={{ color: '#C13221' }}>D</span>étail {index + 1}
                      </DetailLabel>
                      <DetailValue>
                        {detail.title ? <strong>{detail.title}: </strong> : null}
                        {detail.value}
                      </DetailValue>
                    </DetailRow>
                  ))}
              </DetailsList>
            )}
          </ProjectInfo>
          
          <ProjectGallery>
            {project.images && project.images.map((image, index) => (
              <GalleryItem key={index}>
                <GalleryImage src={image} alt={`${project.title} - Image ${index + 1}`} />
              </GalleryItem>
            ))}
          </ProjectGallery>
        </ProjectContent>
        
        <BackButton>
          <Button variant="outline" onClick={() => navigate('/galerie')}>
            Retour aux projets
          </Button>
        </BackButton>
      </SectionContainer>
    </ProjectDetailContainer>
  );
};

export default ProjectDetailPage;
